import React, { useState, useRef, useEffect } from 'react';
// Header is provided by LayoutWithHeader in App.js
import { useNavigate, useLocation } from 'react-router-dom';
import './HostDinnerDetails.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Helmet } from "react-helmet";
import "bootstrap/dist/css/bootstrap.min.css";
import { db, auth } from '../../firebase';
import { collection, addDoc, updateDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getUserProfile } from '../../utils/firestoreUser';
import { getLatLngFromAddress } from '../../utils/geocoding';

// Head section component for meta and visible title
function HeadSection({ isEditing }) {
    return (
        <>
            <Helmet>
                <title>{isEditing ? 'Edit Shabbat Dinner' : 'Host a Shabbat Dinner'} | Shabbat Abroad</title>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="description" content={isEditing ? 'Edit your Shabbat dinner details and welcome guests from around the world.' : 'Host a Shabbat dinner and welcome guests from around the world. Share your home, your table, and the Shabbat experience.'} />
                <link rel="icon" type="image/png" href="/logo2.png" />
            </Helmet>
            <div style={{
                width: '100%',
                textAlign: 'center',
                marginBottom: '2rem',
                fontWeight: 700,
                fontSize: '2rem',
                color: '#15316b',
                letterSpacing: '0.5px',
                fontFamily: 'Inter, sans-serif'
            }}>
                {isEditing ? 'Edit Shabbat Dinner' : 'Host a Shabbat Dinner'}
            </div>
        </>
    );
}

// Helper to allow only Fridays in DatePicker
const isFriday = (date) => date.getDay() === 5;

// Helper function to validate time format
const isValidTime = (time) => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
};

// Helper to get the Nth Friday of a given month and year
const getNthFridayOfMonth = (year, month, n) => {
    let count = 0;
    for (let day = 1; day <= 31; day++) {
        let date = new Date(year, month, day, 12, 0, 0);
        if (date.getMonth() !== month) break;

        if (date.getDay() === 5) {
            count++;
            if (count === n) {
                return date;
            }
        }
    }
    return null;
};

// Helper function to generate recurring dates based on frequency
const getRecurringDates = (frequency, initialSelectedDate = null) => {
    const dates = [];
    let tempCurrentDate;

    if (initialSelectedDate) {
        const [year, month, day] = initialSelectedDate.split('-').map(Number);
        tempCurrentDate = new Date(year, month - 1, day, 12, 0, 0);
    } else {
        tempCurrentDate = new Date();
        tempCurrentDate.setHours(12, 0, 0, 0);
    }

    let effectiveStartDate = new Date(tempCurrentDate);
    let today = new Date();
    today.setHours(0, 0, 0, 0);

    while (effectiveStartDate.getDay() !== 5 || effectiveStartDate.getTime() < today.getTime()) {
        effectiveStartDate.setDate(effectiveStartDate.getDate() + 1);
        effectiveStartDate.setHours(12, 0, 0, 0);
    }

    let currentIterationDate = new Date(effectiveStartDate);

    for (let i = 0; i < 12; i++) {
        if (!currentIterationDate) {
            break;
        }
        dates.push(currentIterationDate.toISOString().split('T')[0]);

        if (frequency === 'monthly') {
            let nextMonth = new Date(currentIterationDate);
            nextMonth.setMonth(currentIterationDate.getMonth() + 1);
            nextMonth.setHours(12, 0, 0, 0);

            let nextFourthFriday = getNthFridayOfMonth(nextMonth.getFullYear(), nextMonth.getMonth(), 4);

            while (!nextFourthFriday || nextFourthFriday.getTime() <= currentIterationDate.getTime()) {
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                nextMonth.setHours(12, 0, 0, 0);
                nextFourthFriday = getNthFridayOfMonth(nextMonth.getFullYear(), nextMonth.getMonth(), 4);

                if (nextMonth.getMonth() === currentIterationDate.getMonth() && nextMonth.getFullYear() === currentIterationDate.getFullYear() + 1) {
                    console.error("Could not find a valid 4th Friday within a year for monthly recurring option. Breaking generation.");
                    currentIterationDate = null;
                    break;
                }
            }
            currentIterationDate = nextFourthFriday;
        } else {
            const daysToAdd = {
                'weekly': 7,
                'biweekly': 14,
                'triweekly': 21
            }[frequency];
            currentIterationDate.setDate(currentIterationDate.getDate() + daysToAdd);
            currentIterationDate.setHours(12, 0, 0, 0);
        }
    }
    return dates;
};

export default function HostDinnerDetails() {
    const navigate = useNavigate();
    const location = useLocation();
    const addressData = location.state?.addressData || {};
    const [isEditing, setIsEditing] = useState(false);
    const [dinnerId, setDinnerId] = useState(null);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        mealStyle: [],
        languages: '',
        dinnerTime: '',
        maxGuests: '',
        description: '',
        availableDates: [],
        kosherType: '',
        recurringOption: ''
    });
    const timePickerRef = useRef();
    const [date, setDate] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [time, setTime] = useState("");
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [showGuestsDropdown, setShowGuestsDropdown] = useState(false);
    const [showKosherDropdown, setShowKosherDropdown] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // Check if we're editing an existing dinner
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('id');
        if (id) {
            setIsEditing(true);
            setDinnerId(id);
            loadExistingDinner(id);
        }
    }, [location.search]);

    // Load existing dinner data
    const loadExistingDinner = async (id) => {
        try {
            setLoading(true);
            const dinnerRef = doc(db, 'dinners', id);
            const dinnerSnap = await getDoc(dinnerRef);

            if (dinnerSnap.exists()) {
                const dinnerData = dinnerSnap.data();
                const details = dinnerData.dinnerDetails || {};

                setFormData({
                    mealStyle: details.mealStyle || [],
                    languages: details.languages || '',
                    dinnerTime: details.dinnerTime || '',
                    maxGuests: details.maxGuests?.toString() || '',
                    description: details.description || '',
                    availableDates: details.availableDates || [],
                    kosherType: details.kosherType || '',
                    recurringOption: details.recurringOption || ''
                });

                setTime(details.time || "");

                // Set the first date as selected in the date picker
                if (details.availableDates && details.availableDates.length > 0) {
                    setDate(new Date(details.availableDates[0]));
                }
            }
        } catch (error) {
            console.error('Error loading dinner:', error);
            alert('Failed to load dinner details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateSelect = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        setFormData(prev => {
            const currentDates = [...prev.availableDates];
            const dateIndex = currentDates.indexOf(formattedDate);

            if (dateIndex === -1) {
                // Add the date if it's not already selected
                currentDates.push(formattedDate);
            } else {
                // Remove the date if it's already selected
                currentDates.splice(dateIndex, 1);
            }

            return {
                ...prev,
                availableDates: currentDates
            };
        });
        setDate(date);
    };

    const handleTimeChange = (e) => {
        const value = e.target.value;
        if (!value) {
            setTime("");
            return;
        }

        if (value.length < formData.dinnerTime.length) {
            setTime(value);
            return;
        }

        if (value.length === 2 && !value.includes(":")) {
            setTime(value + ":");
            return;
        }

        if (value.length <= 5) {
            setTime(value);
        }
    };

    const handleTimeBlur = () => {
        if (time && !isValidTime(time)) {
            setTime("");
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                timePickerRef.current &&
                !timePickerRef.current.contains(event.target)
            ) {
                setShowTimePicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleRecurringOptionChange = (option, initialDate = null) => {
        if (option === '') {
            setFormData(prev => ({ ...prev, recurringOption: '', availableDates: [] }));
            return;
        }

        const dates = getRecurringDates(option, initialDate);
        setFormData(prev => ({ ...prev, recurringOption: option, availableDates: dates }));
    };

    const handleMealStyleChange = (style) => {
        setFormData(prev => {
            const currentMealStyles = [...prev.mealStyle];

            if (style === 'Open to all') {
                if (currentMealStyles.includes(style)) {
                    // If 'Open to all' is deselected, clear all options
                    return { ...prev, mealStyle: [] };
                } else {
                    // If 'Open to all' is selected, add all options
                    const allStyles = ["Family", "Students", "Traditional", "Large Group", "Vegan", "Vegetarian", "Intimate", "Open to all"];
                    return { ...prev, mealStyle: allStyles };
                }
            } else {
                // For other styles, toggle as before
                return {
                    ...prev,
                    mealStyle: currentMealStyles.includes(style)
                        ? currentMealStyles.filter((s) => s !== style)
                        : [...currentMealStyles, style]
                };
            }
        });
    };

    const handleSubmit = async () => {
        try {
            if (!auth.currentUser) {
                throw new Error('User must be logged in to host a dinner');
            }

            const userProfile = await getUserProfile(auth.currentUser.uid);

            // --- Geocoding ---
            const coords = await getLatLngFromAddress({
                city: addressData.city,
                neighborhood: addressData.neighborhood,
                country: addressData.country
            });

            const dinnerData = {
                address: {
                    country: addressData.country || '',
                    city: addressData.city || '',
                    neighborhood: addressData.neighborhood || '',
                    street: addressData.street || '',
                    houseNumber: addressData.house || '',
                    lat: coords?.lat || '',
                    lng: coords?.lng || ''
                },
                dinnerDetails: {
                    date: formData.availableDates[0],
                    time: time,
                    maxGuests: parseInt(formData.maxGuests),
                    kosherType: formData.kosherType,
                    mealStyle: formData.mealStyle,
                    description: formData.description,
                    recurringOption: formData.recurringOption,
                    availableDates: formData.availableDates
                },
                hostId: auth.currentUser.uid,
                hostEmail: auth.currentUser.email,
                updatedAt: serverTimestamp()
            };

            if (isEditing && dinnerId) {
                // Update existing dinner
                const dinnerRef = doc(db, 'dinners', dinnerId);
                await updateDoc(dinnerRef, dinnerData);
                console.log('Dinner updated with ID:', dinnerId);
            } else {
                // Create new dinner
                dinnerData.createdAt = serverTimestamp();
                const docRef = await addDoc(collection(db, 'dinners'), dinnerData);
                console.log('Dinner saved with ID:', docRef.id);
            }

            setShowModal(true);
        } catch (error) {
            console.error('Error saving dinner:', error);
            alert('Failed to save dinner details. Please try again.');
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        navigate('/Home');
    };

    if (loading) {
        return (
            <div className="container-fluid min-vh-100 d-flex flex-column p-0" style={{ background: 'none' }}>
                <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading dinner details...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid min-vh-100 d-flex flex-column p-0" style={{ background: 'none' }}>
            <HeadSection isEditing={isEditing} />
            <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                <div className="card shadow-lg p-4 rounded-4 w-100" style={{ maxWidth: 800, Height:850, background: '#fff', position: 'relative', marginBottom: 50 }}>
                    <div className="d-flex align-items-center mb-3" style={{ position: 'relative' }}>
                        <span
                            className="return-arrow"
                            style={{ cursor: 'pointer', fontSize: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                            onClick={() => isEditing ? navigate('/profile?tab=dinner%20cards') : navigate('/HostDinnerAddress')}
                        >
                            &#8592;
                            <span className="return-explanation" style={{ fontSize: '0.95rem', color: '#666', fontFamily: 'Inter, sans-serif', marginTop: 2 }}>
                                {isEditing ? 'return to profile' : 'return to Address'}
                            </span>
                        </span>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                            <span role="img" aria-label="house" style={{ fontSize: 32, marginRight: 12 }}>🏠</span>
                            <span className="host-dinner-link mb-0" style={{ fontWeight: 600, color: "#15316b", marginLeft: 8, textAlign: "left" }}>
                                {isEditing ? 'Edit Dinner' : 'Host a Dinner'}
                            </span>
                        </div>
                    </div>
                    <h2 className="mb-3" style={{ fontWeight: "bold", fontSize: "1.3rem", marginTop: 30, textAlign: "center" }}>
                        Help us get to know your Shabbat dinner
                    </h2>

                    {/* Date and Time Section */}
                    <div className="mb-4" style={{ textAlign: "left" }}>
                        <div className="row g-3">
                            {/* Date Selection */}
                            <div className="col-md-6">
                                <div style={{ fontWeight: "bold", color: "#15316b", fontSize: "1.1rem", marginBottom: "1rem" }}>
                                    When would you like to host?
                                </div>
                                <div className="position-relative">
                                    <input
                                        type="text"
                                        readOnly
                                        value={formData.availableDates.length > 0
                                            ? formData.availableDates.map(date =>
                                                new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                            ).join(', ')
                                            : "Select date"}
                                        placeholder="Pick Friday date"
                                        onClick={() => setShowDatePicker(true)}
                                        className="form-control"
                                        style={{ cursor: "pointer" }}
                                    />
                                    {showDatePicker && (
                                        <div style={{
                                            position: 'absolute',
                                            zIndex: 1000,
                                            backgroundColor: 'white',
                                            borderRadius: '12px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                            padding: '1rem'
                                        }}>
                                            <DatePicker
                                                selected={date}
                                                onChange={(d) => { handleDateSelect(d); }}
                                                filterDate={isFriday}
                                                inline
                                                minDate={new Date()}
                                                onClickOutside={() => setShowDatePicker(false)}
                                                highlightDates={formData.availableDates.map(date => new Date(date))}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Recurring Option */}
                                <div className="mt-3">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="recurringCheck"
                                            checked={formData.recurringOption !== ''}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    // When enabling recurring, use the first selected date as the initial date
                                                    const initialDate = formData.availableDates.length > 0 ? formData.availableDates[0] : null;
                                                    handleRecurringOptionChange('weekly', initialDate);
                                                } else {
                                                    handleRecurringOptionChange('');
                                                }
                                            }}
                                        />
                                        <label className="form-check-label" htmlFor="recurringCheck">
                                            Host regularly
                                        </label>
                                    </div>
                                    {formData.recurringOption !== '' && (
                                        <select
                                            className="form-select mt-2"
                                            value={formData.recurringOption}
                                            onChange={(e) => {
                                                const initialDate = formData.availableDates.length > 0 ? formData.availableDates[0] : null;
                                                handleRecurringOptionChange(e.target.value, initialDate);
                                            }}
                                            style={{ maxWidth: '200px' }}
                                        >
                                            <option value="weekly">Every week</option>
                                            <option value="biweekly">Every two weeks</option>
                                            <option value="triweekly">Every three weeks</option>
                                            <option value="monthly">Every month</option>
                                        </select>
                                    )}
                                </div>
                            </div>

                            {/* Dinner Time */}
                            <div className="col-md-6">
                                <div style={{ fontWeight: "bold", color: "#15316b", fontSize: "1.1rem", marginBottom: "1rem" }}>
                                    Dinner Time
                                </div>
                                <div className="position-relative">
                                    <input
                                        type="text"
                                        readOnly
                                        value={time}
                                        placeholder="Select time"
                                        onClick={() => setShowTimePicker(!showTimePicker)}
                                        className="form-control"
                                        style={{ cursor: "pointer" }}
                                    />
                                    {showTimePicker && (
                                        <ul style={{
                                            position: "absolute",
                                            background: "#fff",
                                            border: "1px solid #ccc",
                                            borderRadius: 8,
                                            zIndex: 1000,
                                            listStyle: "none",
                                            margin: 0,
                                            padding: 0,
                                            width: "100%",
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                        }}>
                                            {Array.from({ length: 24 }, (_, i) => {
                                                const hour = i.toString().padStart(2, '0');
                                                return [`${hour}:00`, `${hour}:30`];
                                            }).flat().map(timeOption => (
                                                <li
                                                    key={timeOption}
                                                    style={{
                                                        padding: "8px 12px",
                                                        cursor: "pointer",
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                    onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                    onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                                    onClick={() => { setTime(timeOption); setShowTimePicker(false); }}
                                                >
                                                    {timeOption}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Max Guests, Kosher Type, and Meal Style */}
                    <div className="mb-4">
                        <div className="row g-3 align-items-stretch">
                            {/* Left Column: Max Guests & Kosher Type */}
                            <div className="col-md-6">
                                {/* Max Guests Section */}
                                <div>
                                    <div style={{ fontWeight: "bold", color: "#15316b", fontSize: "1.1rem", marginBottom: "1rem" }}>
                                        Maximum Number of Guests
                                    </div>
                                    <div className="position-relative" style={{ maxWidth: '200px' }}>
                                        <input
                                            type="text"
                                            readOnly
                                            value={formData.maxGuests}
                                            placeholder="Number"
                                            onClick={() => setShowGuestsDropdown(!showGuestsDropdown)}
                                            className="form-control"
                                            style={{ cursor: "pointer" }}
                                        />
                                        {showGuestsDropdown && (
                                            <ul style={{
                                                position: "absolute",
                                                background: "#fff",
                                                border: "1px solid #ccc",
                                                borderRadius: 8,
                                                zIndex: 1000,
                                                listStyle: "none",
                                                margin: 0,
                                                padding: 0,
                                                width: "100%",
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                            }}>
                                                {[...Array(10)].map((_, i) => (
                                                    <li
                                                        key={i + 1}
                                                        style={{
                                                            padding: "8px 12px",
                                                            cursor: "pointer",
                                                            transition: 'background-color 0.2s ease'
                                                        }}
                                                        onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        onClick={() => { setFormData(prev => ({ ...prev, maxGuests: i + 1 })); setShowGuestsDropdown(false); }}
                                                    >
                                                        {i + 1}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                {/* Kosher Type Section */}
                                <div style={{ marginTop: "1rem" }}>
                                    <div style={{ fontWeight: "bold", color: "#15316b", fontSize: "1.1rem", marginBottom: "1rem" }}>
                                        Kosher Type
                                    </div>
                                    <div className="position-relative" style={{ maxWidth: '200px' }}>
                                        <input
                                            type="text"
                                            readOnly
                                            value={formData.kosherType}
                                            placeholder="Select a type"
                                            onClick={() => setShowKosherDropdown(!showKosherDropdown)}
                                            className="form-control"
                                            style={{ cursor: "pointer" }}
                                        />
                                        {showKosherDropdown && (
                                            <ul style={{
                                                position: "absolute",
                                                background: "#fff",
                                                border: "1px solid #ccc",
                                                borderRadius: 8,
                                                zIndex: 1000,
                                                listStyle: "none",
                                                margin: 0,
                                                padding: 0,
                                                width: "100%",
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                            }}>
                                                {["Kosher (basic)", "Kosher Mehadrin", "Non Kosher"].map((type) => (
                                                    <li
                                                        key={type}
                                                        style={{
                                                            padding: "8px 12px",
                                                            cursor: "pointer",
                                                            transition: 'background-color 0.2s ease'
                                                        }}
                                                        onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        onClick={() => { setFormData(prev => ({ ...prev, kosherType: type })); setShowKosherDropdown(false); }}
                                                    >
                                                        {type}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Right Column: Meal Style */}
                            <div className="col-md-6 d-flex flex-column justify-content-between">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div style={{ fontWeight: "bold", color: "#15316b", fontSize: "1.1rem" }}>
                                        Meal Style
                                    </div>
                                    {formData.mealStyle.length > 0 && (
                                        <button
                                            className="btn btn-link text-decoration-none p-0"
                                            style={{ color: '#666', fontSize: '0.9rem' }}
                                            onClick={() => setFormData(prev => ({ ...prev, mealStyle: [] }))}
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                <div className="bg-light rounded-3 p-3 flex-grow-1" style={{
                                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                                    backgroundColor: '#f8f9fa',
                                    minHeight: 120
                                }}>
                                    {["Family", "Students", "Traditional", "Large Group", "Vegan", "Vegetarian", "Intimate", "Open to all"].map((styleOption) => (
                                        <label key={styleOption}>
                                            <input
                                                type="checkbox"
                                                name="dinnerStyle"
                                                value={styleOption}
                                                checked={formData.mealStyle.includes(styleOption)}
                                                onChange={() => handleMealStyleChange(styleOption)}
                                                style={{ display: 'none' }}
                                            />
                                            <span style={{
                                                marginRight: 16,
                                                marginBottom: 8,
                                                display: 'inline-block',
                                                padding: '6px 12px',
                                                borderRadius: '20px',
                                                backgroundColor: formData.mealStyle.includes(styleOption) ? '#ffb97a' : 'transparent',
                                                color: formData.mealStyle.includes(styleOption) ? '#fff' : '#333',
                                                transition: 'all 0.2s ease',
                                                cursor: 'pointer',
                                                border: `1px solid ${formData.mealStyle.includes(styleOption) ? '#ffb97a' : '#dee2e6'}`
                                            }}>{styleOption}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* About Section */}
                        <div className="mb-4">
                            <div style={{
                                fontWeight: "bold",
                                color: "#15316b",
                                fontSize: "1.1rem",
                                marginBottom: "1rem"
                            }}>
                                A few words about your Shabbat dinner:
                            </div>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Share a little about your home, the atmosphere, and your dinner style."
                                className="form-control"
                                style={{
                                    minHeight: 120,
                                    fontSize: "1rem",
                                    padding: '1rem',
                                    borderRadius: '12px',
                                    border: '1px solid #dee2e6',
                                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                                    resize: 'none'
                                }}
                            />
                        </div>

                        {/* Submit Button */}
                        <div className="d-flex justify-content-center">
                            <button
                                className="btn btn-warning px-4 py-2 rounded-pill fw-bold text-white"
                                style={{
                                    background: '#ffb97a',
                                    border: 'none',
                                    marginTop: 24,
                                    width: 200,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={handleSubmit}
                                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                            >
                                {isEditing ? 'Update Dinner' : 'All set!'}
                            </button>
                        </div>

                        {/* Modal */}
                        {showModal && (
                            <div className="modal-overlay d-flex align-items-center justify-content-center" style={{
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                backdropFilter: 'blur(4px)'
                            }}>
                                <div className="modal-content" style={{
                                    maxWidth: 400,
                                    width: '100%',
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    padding: '2rem'
                                }}>
                                    <button
                                        className="modal-close"
                                        onClick={handleModalClose}
                                        aria-label="Close"
                                        style={{
                                            position: 'absolute',
                                            right: '1rem',
                                            top: '1rem',
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.5rem',
                                            color: '#666',
                                            cursor: 'pointer',
                                            padding: '0.5rem',
                                            lineHeight: 1
                                        }}
                                    >
                                        &times;
                                    </button>
                                    <div style={{ textAlign: "center", marginTop: 17 }}>
                                        <div style={{
                                            fontWeight: 600,
                                            fontSize: "1.5rem",
                                            color: '#15316b',
                                            marginBottom: '1rem'
                                        }}>{isEditing ? 'Updated!' : 'All done!'}</div>
                                        <div style={{
                                            marginTop: 12,
                                            color: '#666',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.5
                                        }}>
                                            {isEditing
                                                ? 'Your dinner has been successfully updated!'
                                                : 'We\'ll take it from here and let you know as soon as you get a request'
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}